import React from 'react';
import { Input } from '../../ui/Input/Input';

export interface InvoiceNumberInputProps {
  /** Input id for `<label htmlFor>` pairing in the consumer. */
  id?: string;
  /** Controlled value (without the `#` glyph). */
  value: string;
  /** Called with the next value; the `#` glyph is stripped before propagation. */
  onChange: (next: string) => void;
  /** Optional placeholder shown after the `#` glyph. */
  placeholder?: string;
  /** Disable typing. */
  disabled?: boolean;
  /** Additional Tailwind classes appended to the inner `<Input>`. */
  className?: string;
}

/**
 * Invoice-number text input with a leading `#` glyph adornment.
 *
 * Single source of truth for the "type your accounting-system
 * invoice number" UX. Visually mirrors a currency prefix
 * (`PriceInput`) so the `#` reads as a static label, not a
 * character the user has to type. Pasted `#` characters are
 * stripped on input so the stored value matches vendor invoice
 * strings exactly.
 *
 * Used by:
 *   - `Financing` apply-form (`InvoiceFieldset` – manual external
 *     invoice number for the financing application).
 *   - `EligibleLegsDrawer` step 2 (carrier-dashboard CRM
 *     "Create invoice from delivered orders") – seeds the new
 *     invoice's `invoiceProof.refNumber`.
 *
 * RTL note: the glyph uses `start-*` / `ps-*` logical utilities,
 * so it auto-flips to the right side under `<html dir="rtl">`
 * without any `isRTL` runtime check.
 */
export function InvoiceNumberInput({
  id,
  value,
  onChange,
  placeholder,
  disabled,
  className,
}: InvoiceNumberInputProps) {
  return (
    <div className="relative">
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3 text-sm font-medium text-gray-400"
      >
        #
      </span>
      <Input
        id={id}
        value={value}
        onChange={e => onChange(e.target.value.replace(/#/g, '').trimStart())}
        placeholder={placeholder}
        disabled={disabled}
        className={['ps-7', className].filter(Boolean).join(' ')}
      />
    </div>
  );
}
